// src/components/security/sessions.ts
/**
 * Turning `GET /api/admin/security/sessions` into something a human can act on.
 *
 * The endpoint answers TOKEN ROWS, and one login mints two of them — a BEARER and a REFRESH, for the
 * same user, seconds apart. Rendered raw, a session list of three logged-in people is six lines that
 * look like six sessions, and "Odhlásiť reláciu" on one of them leaves the other half of the same
 * login alive. Grouping the pairs back into one line per login is therefore not cosmetic: it is what
 * makes the revoke button mean what its label says.
 *
 * WHY THIS MODULE IS PURE, LIKE `mfaContract.ts`. It imports one type and nothing else — no axios
 * (jest 27 under CRA 5 cannot resolve axios 1.7.8's ESM entry), no store (importing it reads
 * localStorage and builds the state tree as a side effect), no React. The pairing rule and the
 * timestamp parsing are the two things here that can be wrong in a way nobody notices by looking, so
 * they live where `sessions.test.ts` can pin them down.
 */
import { ActiveSession } from '../../security/mfaContract';

/**
 * How far apart the two rows of one login may be.
 *
 * `AuthenticationService` saves the bearer and the refresh row back to back in one request, so the
 * real gap is milliseconds; five seconds is slack for a loaded server, not a guess at the interval.
 * It stays well under the time between two deliberate logins by the same person, which is what the
 * window has to stay under to avoid merging two sessions into one line.
 */
export const REFRESH_PAIR_WINDOW_MILLIS = 5000;

/** One login: the rows it produced, and the facts the table shows about it. */
export interface SessionGroup {
    /** React key. The lowest token id in the group — token ids are unique across the whole table. */
    key: string;
    userId: number;
    userEmail: string;
    /** The group's time, taken from the BEARER when there is one. Null when the server sent none. */
    createdAt: string | null;
    /** Whether this login has passed its second factor (`t_token.c_mfa_verified` on the bearer). */
    mfaVerified: boolean;
    /** Every row in the group, BEARER first — display order. `revocationOrder` reverses it. */
    tokens: ActiveSession[];
}

/* ---------------- timestamps ---------------- */

/**
 * `2026-09-10T03:08:12.123456` — a zone-less `LocalDateTime` in SERVER time, with up to nine
 * fractional digits.
 *
 * Parsed by hand rather than with `Date.parse`, which is the whole reason this function exists.
 * `Date.parse('2026-09-10T03:08:12.123456')` is not specified: the ISO grammar in ECMA-262 allows
 * exactly three fractional digits, so a six-digit fraction falls through to each engine's own
 * fallback parser — several of which read it as 123456 MILLISECONDS and hand back a timestamp two
 * minutes late, and some of which return NaN. A two-minute error is exactly the size that turns a
 * correct pair into two rows.
 */
const TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:[.,](\d+))?/;

interface TimeParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millis: number;
}

function timeParts(createdAt: string | null | undefined): TimeParts | null {
    if (typeof createdAt !== 'string') {
        return null;
    }
    const match = TIMESTAMP.exec(createdAt.trim());
    if (!match) {
        return null;
    }
    // Truncate the fraction to milliseconds rather than rounding: the value is only ever used for a
    // difference and for display, and truncation is what every other reader of this column does.
    const fraction = match[7] ? (match[7] + '000').slice(0, 3) : '000';
    return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour: Number(match[4]),
        minute: Number(match[5]),
        second: match[6] ? Number(match[6]) : 0,
        millis: Number(fraction),
    };
}

/**
 * The timestamp as a number, or null when it could not be read.
 *
 * Deliberately computed with `Date.UTC` even though the value is server LOCAL time. Nothing here
 * converts a zone — the number is used for differences and for ordering only, never rendered — and
 * pinning the arithmetic to UTC keeps it free of the browser zone's DST discontinuities. Building it
 * with `new Date(y, m, d, …)` instead would make two rows an hour apart on the night the clocks go
 * back, and zero apart on the night they go forward.
 */
export function toMillis(createdAt: string | null | undefined): number | null {
    const parts = timeParts(createdAt);
    if (!parts) {
        return null;
    }
    return Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
        parts.millis
    );
}

function pad2(value: number): string {
    return value < 10 ? '0' + value : String(value);
}

/**
 * "10. 9. 2026 03:08", the Slovak short form, or "—" when there is no readable timestamp.
 *
 * Formatted straight from the parsed components, never through a `Date`, so the number shown is the
 * one the server stored. Seconds are dropped: the column answers "when did this person log in",
 * which no reader needs to the second, and dropping them keeps the pair of columns narrow.
 */
export function formatSessionTime(createdAt: string | null | undefined): string {
    const parts = timeParts(createdAt);
    if (!parts) {
        return '—';
    }
    return (
        parts.day +
        '. ' +
        parts.month +
        '. ' +
        parts.year +
        ' ' +
        pad2(parts.hour) +
        ':' +
        pad2(parts.minute)
    );
}

/* ---------------- grouping ---------------- */

function isBearer(row: ActiveSession): boolean {
    return row.tokenType === 'BEARER';
}

function isPairableRefresh(row: ActiveSession): boolean {
    return row.tokenType === 'REFRESH' && toMillis(row.createdAt) !== null;
}

/** Oldest first, rows without a timestamp last, then by id. A total order, so the result never
 *  depends on the order the API listed the rows in. */
function byTimeThenId(a: ActiveSession, b: ActiveSession): number {
    const at = toMillis(a.createdAt);
    const bt = toMillis(b.createdAt);
    if (at === null && bt === null) {
        return a.tokenId - b.tokenId;
    }
    if (at === null) {
        return 1;
    }
    if (bt === null) {
        return -1;
    }
    if (at !== bt) {
        return at - bt;
    }
    return a.tokenId - b.tokenId;
}

function startGroup(row: ActiveSession): SessionGroup {
    return {
        key: String(row.tokenId),
        userId: row.userId,
        userEmail: typeof row.userEmail === 'string' ? row.userEmail : '',
        createdAt: row.createdAt,
        mfaVerified: row.mfaVerified === true,
        tokens: [row],
    };
}

function holdsRefresh(group: SessionGroup): boolean {
    for (let i = 0; i < group.tokens.length; i++) {
        if (group.tokens[i].tokenType === 'REFRESH') {
            return true;
        }
    }
    return false;
}

/** Newest first, groups without a timestamp last, then by e-mail, then by key. The last two make it
 *  a total order: `Array.prototype.sort` is only guaranteed stable from ES2019, and this list is
 *  re-rendered after every revoke. */
function byGroupOrder(a: SessionGroup, b: SessionGroup): number {
    const at = toMillis(a.createdAt);
    const bt = toMillis(b.createdAt);
    if (at !== null && bt === null) {
        return -1;
    }
    if (at === null && bt !== null) {
        return 1;
    }
    if (at !== null && bt !== null && at !== bt) {
        return bt - at;
    }
    if (a.userEmail !== b.userEmail) {
        return a.userEmail < b.userEmail ? -1 : 1;
    }
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

/**
 * One `SessionGroup` per login.
 *
 * The rule, and why each half of it is there:
 *
 * - **Every BEARER opens a group.** The bearer is the row that carries `mfaVerified` and the row the
 *   app authenticates with, so it is the session as far as a reader is concerned.
 * - **A REFRESH joins the same user's nearest bearer within five seconds**, and a group takes at most
 *   ONE refresh — a login mints exactly one, so a second candidate is always a different login. Both
 *   halves matter: without "same user" two people logging in together merge; without "at most one"
 *   two logins a second apart collapse into one line with three tokens and one that looks orphaned.
 * - **Anything left over stands alone**: a refresh whose bearer has already been revoked, a row the
 *   server sent with a null `createdAt`, a token type this client has never heard of. Standing alone
 *   is the honest rendering — the row exists and can be revoked — and it is strictly better than
 *   hiding it, because the point of this table is to show every live token.
 *
 * Refreshes are matched oldest-first against bearers sorted the same way, so the pairing is a
 * function of the data and not of the order the API happened to return.
 */
export function groupSessions(rows: ActiveSession[]): SessionGroup[] {
    const safe: ActiveSession[] = [];
    if (Array.isArray(rows)) {
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (row && typeof row === 'object') {
                safe.push(row);
            }
        }
    }

    const bearers: ActiveSession[] = [];
    const refreshes: ActiveSession[] = [];
    const loners: ActiveSession[] = [];
    for (let i = 0; i < safe.length; i++) {
        const row = safe[i];
        if (isBearer(row)) {
            bearers.push(row);
        } else if (isPairableRefresh(row)) {
            refreshes.push(row);
        } else {
            loners.push(row);
        }
    }
    bearers.sort(byTimeThenId);
    refreshes.sort(byTimeThenId);

    const groups: SessionGroup[] = [];
    for (let i = 0; i < bearers.length; i++) {
        groups.push(startGroup(bearers[i]));
    }

    for (let i = 0; i < refreshes.length; i++) {
        const refresh = refreshes[i];
        const at = toMillis(refresh.createdAt) as number;
        let best: SessionGroup | null = null;
        let bestDelta = 0;
        for (let g = 0; g < groups.length; g++) {
            const group = groups[g];
            if (group.userId !== refresh.userId || holdsRefresh(group)) {
                continue;
            }
            const groupAt = toMillis(group.createdAt);
            if (groupAt === null) {
                continue;
            }
            const delta = Math.abs(groupAt - at);
            if (delta > REFRESH_PAIR_WINDOW_MILLIS) {
                continue;
            }
            if (best === null || delta < bestDelta) {
                best = group;
                bestDelta = delta;
            }
        }
        if (best) {
            best.tokens.push(refresh);
        } else {
            loners.push(refresh);
        }
    }

    for (let i = 0; i < loners.length; i++) {
        groups.push(startGroup(loners[i]));
    }

    // The key is only final once the group is: a lone refresh keys off its own id, and a paired one
    // may carry the lower of the two.
    for (let i = 0; i < groups.length; i++) {
        const group = groups[i];
        let lowest = group.tokens[0].tokenId;
        let verified = false;
        for (let t = 0; t < group.tokens.length; t++) {
            if (group.tokens[t].tokenId < lowest) {
                lowest = group.tokens[t].tokenId;
            }
            if (group.tokens[t].mfaVerified === true) {
                verified = true;
            }
        }
        group.key = String(lowest);
        group.mfaVerified = verified;
    }

    groups.sort(byGroupOrder);
    return groups;
}

/**
 * The order the group's tokens must be revoked in: everything else first, the BEARER last.
 *
 * This is the difference between "ended my own session cleanly" and "ended it halfway". Revoking a
 * row takes an authenticated request, and the row being revoked may be the very token making it —
 * the API deliberately never returns the token string, so a client cannot tell which listed BEARER
 * is its own. Killing the bearer first would leave the second request authenticating with a token
 * the first request just revoked: the filter would refuse it, the session-over rule in `securityApi`
 * would sign the admin out mid-loop, and the refresh row would survive. Leaving the bearer for last
 * means every request but the final one is made with a live token, and the final one is answered
 * before it takes effect.
 */
export function revocationOrder(group: SessionGroup): number[] {
    const ids: number[] = [];
    for (let i = 0; i < group.tokens.length; i++) {
        if (group.tokens[i].tokenType !== 'BEARER') {
            ids.push(group.tokens[i].tokenId);
        }
    }
    for (let i = 0; i < group.tokens.length; i++) {
        if (group.tokens[i].tokenType === 'BEARER') {
            ids.push(group.tokens[i].tokenId);
        }
    }
    return ids;
}

/** Slovak names for the two token types, for the "Tokeny" column. An unknown type is shown as the
 *  server sent it rather than dropped — this table exists to account for every live row. */
export function tokenTypeLabel(tokenType: string): string {
    if (tokenType === 'BEARER') {
        return 'prístupový';
    }
    if (tokenType === 'REFRESH') {
        return 'obnovovací';
    }
    return tokenType;
}

/** "prístupový + obnovovací" — what one login usually looks like. */
export function tokenSummary(group: SessionGroup): string {
    const labels: string[] = [];
    for (let i = 0; i < group.tokens.length; i++) {
        labels.push(tokenTypeLabel(group.tokens[i].tokenType));
    }
    return labels.join(' + ');
}
